/**
 * CoolerCallouts Web Component
 * Handles video playback logic
 */
class CoolerCallouts extends HTMLElement {
  constructor() {
    super();
    this.video = null;
    this.secondVideo = null;
    this.endTarget = null;
    this.endObserver = null;
    this.secondVideoObserver = null;
    this.videoPlayed = false;
    this.secondVideoPlayed = false;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  async init() {
    this.video = this.querySelector('.cooler-callouts__main-video');
    this.secondVideo = this.querySelector('.cooler-callouts__second-video');
    this.endTarget = this.querySelector('.cooler-callouts__end-target');

    await this.setupMainVideo();
    await this.setupSecondVideo();

    this.setupVideoObserver();
    this.setupFadeInAnimations();
  }

  async setupMainVideo() {
    if (!this.video) return;

    try {
      this.video.muted = true;
      await this.video.play();
      this.video.currentTime = 0.1;
      this.video.pause();
    } catch (error) {
      console.warn('Main video initial setup failed:', error);
    }
  }

  async setupSecondVideo() {
    if (!this.secondVideo) return;

    try {
      this.secondVideo.muted = true;
      await this.secondVideo.play();
      this.secondVideo.currentTime = 0.1;
      this.secondVideo.pause();
    } catch (error) {
      console.warn('Second video initial setup failed:', error);
    }

    this.secondVideoObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.secondVideoPlayed) {
            this.playSecondVideo();
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 1.0,
      }
    );

    this.secondVideoObserver.observe(this.secondVideo);
  }

  async playSecondVideo() {
    if (!this.secondVideo || this.secondVideoPlayed) return;

    this.secondVideoPlayed = true;

    try {
      this.secondVideo.currentTime = 0;
      this.secondVideo.muted = false;
      await this.secondVideo.play();
    } catch (error) {
      console.warn('Second video playback failed:', error);
      this.secondVideo.muted = true;
      try {
        await this.secondVideo.play();
      } catch (mutedError) {
        console.error('Muted second video playback also failed:', mutedError);
      }
    }
  }

  setupFadeInAnimations() {
    const fadeInElements = this.querySelectorAll('[data-fade-in]');

    if (fadeInElements.length === 0) return;

    const fadeInObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in-visible');
            fadeInObserver.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    fadeInElements.forEach(element => {
      element.style.opacity = '0';
      element.style.transform = 'translateY(20px)';
      element.style.transition = 'opacity 0.8s ease-out, transform 0.8s ease-out';
      fadeInObserver.observe(element);
    });
  }

  setupVideoObserver() {
    if (!this.endTarget) return;

    this.endObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.videoPlayed) {
            this.playVideo();
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      }
    );

    this.endObserver.observe(this.endTarget);
  }

  async playVideo() {
    if (!this.video || this.videoPlayed) return;

    this.videoPlayed = true;

    try {
      this.video.currentTime = 0;
      this.video.muted = false;
      await this.video.play();

      this.video.addEventListener(
        'ended',
        () => {
          this.onVideoEnded();
        },
        { once: true }
      );
    } catch (error) {
      console.warn('Video playback failed:', error);
      this.video.muted = true;
      try {
        await this.video.play();
      } catch (mutedError) {
        console.error('Muted video playback also failed:', mutedError);
      }
    }
  }

  onVideoEnded() {
    console.log('Video playback completed');
  }

  cleanup() {
    if (this.endObserver) {
      this.endObserver.disconnect();
      this.endObserver = null;
    }

    if (this.secondVideoObserver) {
      this.secondVideoObserver.disconnect();
      this.secondVideoObserver = null;
    }

    if (this.video) {
      this.video.pause();
    }

    if (this.secondVideo) {
      this.secondVideo.pause();
    }
  }
}

/**
 * CoolerCallout Web Component
 * Individual callout element with scroll-triggered animations
 */
class CoolerCallout extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.line = null;
    this.lineInner = null;
    this.hasAnimated = false;
  }

  connectedCallback() {
    this.setAttribute('role', 'article');
    this.setAttribute('aria-label', this.getCalloutLabel());

    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.line = this.querySelector('.cooler-callout__line');
    this.lineInner = this.querySelector('.cooler-callout__line-inner');
    this.setupLine();
    this.setupObserver();
  }

  setupLine() {
    if (!this.line) return;
    const length = parseFloat(this.dataset.lineLength) || 100;

    this.line.style.width = `${length}vw`;
    this.line.dataset.targetLength = length;
  }

  setupObserver() {
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.animate();
          }
        });
      },
      {
        root: null,
        rootMargin: '0% 0px -20% 0px',
        threshold: 1,
      }
    );

    this.observer.observe(this);
  }

  animate() {
    this.hasAnimated = true;
    this.classList.add('visible');

    if (this.lineInner) {
      this.lineInner.style.width = '100%';
    }
  }

  getCalloutLabel() {
    const title = this.querySelector('.cooler-callout__title');
    return title ? title.textContent.trim() : 'Product callout';
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

if (!customElements.get('cooler-callouts')) {
  customElements.define('cooler-callouts', CoolerCallouts);
}

if (!customElements.get('cooler-callout')) {
  customElements.define('cooler-callout', CoolerCallout);
}
